<template>
  <div class="uploader">
    <label :for="inputId">
      <div class="drop-zone column justify-center"
           :class="{
                    empty: empty,
                    done: done,
                    uploading: uploading,
                    uploadError: uploadError,
                    validationError: validationError,
                    preloaded: preloaded
                   }">
        <div v-if="empty || validationError" class="column items-center justify-center">
          <default-icon icon="add_a_photo" :color="dropZoneTakePhotoColor" class="camera-icon" v-ripple />
          <div class="r-text-regular r-text-bold" :class="dropZoneTextClass">צילום/העלאת קובץ</div>
        </div>
        <div v-else-if="uploading" class="column items-center justify-center">
          <span class="r-text-bold text-riseup-blue">...הקובץ עולה...</span>
        </div>
        <div v-else-if="uploadError" class="column items-center justify-center">
          <div class="r-text-bold text-riseup-danger-red column items-center">
            <div>משהו קרה וההעלאה לא הצליחה,</div>
            <div>כדאי לנסות שוב</div>
          </div>
        </div>
        <div v-else-if="done" class="image-preview">
          <img :src="currentFile.url"/>
          <default-icon icon="verified" size="sm" color="riseup-white" text-color="riseup-green" font-size="18px" class="success-badge"/>
        </div>
        <div v-else-if="preloaded">
          <div class="row items-center">
            <img class='preloaded-image' :src="require('@/assets/common/social-id-uploaded-successfuly.svg')">
              <span class="text-riseup-green r-text-bold">{{ preloadSuccessText }}</span>
          </div>
        </div>
      </div>
    </label>
    <div class="status-bar">
      <div v-if="empty" class="text-riseup-blue text-center">
        {{ accept.disclaimer }}
      </div>
      <div v-else-if="uploading" class="column items-center">
        <r-linear-progress :value="uploadProgress" color="riseup-blue" reverse rounded size="7px" track-color="riseup-light-blue"/>
        <div class="r-text-body-small text-riseup-blue r-text-bold progress-text"> {{ Math.floor(uploadProgress) }}%</div>
      </div>
      <file-upload-controls v-else-if="done" :input-id="inputId" @removeFile="removeFile" show-success-message />
      <div v-else-if="validationError" class="text-center text-riseup-danger-red r-text-bold">
        {{ validationErrorMessage }}
      </div>
      <file-upload-controls v-else-if="preloaded" :input-id="inputId" @removeFile="removeFile" />
      <file-upload-controls v-else-if="uploadError" :input-id="inputId" @removeFile="removeFile" />
    </div>

    <file-upload
      v-show="false"
      ref="upload"
      :name="fieldName"
      :input-id="inputId"
      :post-action="uploadUrl"
      :multiple="multiple"
      :drop="drop"
      :accept="accept.mimeType"
      v-model="files"
      @input-file="inputFile"
      @input-filter="inputFilter" >
    </file-upload>

  </div>
</template>

<script>
import _ from 'lodash';
import FileUpload from 'vue-upload-component';
import Compressor from 'compressorjs';
import RLinearProgress from '@/components/base/RLinearProgress';
import WithValidation from '@/mixins/WithValidation';
import FileUploadControls from '@/components/FileUploadControls';
import DefaultIcon from '@/components/styled/DefaultIcon';
import FileTypeUnsupported from '@/views/dialogs/FileTypeUnsupported';

export default {
  name: 'SingleFileUploader',
  components: {
    FileUploadControls,
    RLinearProgress,
    FileUpload,
    DefaultIcon,
  },
  data() {
    return {
      files: [],
    };
  },
  mixins: [WithValidation],
  props: {
    uploadUrl: {
      type: String,
      required: true,
    },
    fieldName: String,
    inputId: String,
    multiple: Boolean,
    drop: {
      type: [String, Object, Boolean],
      default: true,
    },
    preloadedFile: String,
    preloadSuccessText: String,
    accept: {
      mimeType: {
        type: String,
        validator: value => value.split('/').length === 2, // Accept only mime types
      },
      disclaimer: String,
    },
  },
  computed: {
    COMPRESSING_MARKER() {
      return 'compressing';
    },
    compressing() {
      return this.currentFile.error === this.COMPRESSING_MARKER;
    },
    /* Upload states START */
    empty() {
      return !this.currentFile && !this.error && !this.preloadedFile;
    },
    uploading() {
      return this.currentFile && (this.currentFile.active || this.compressing);
    },
    uploadError() {
      return this.currentFile && this.currentFile.error && !this.compressing;
    },
    done() {
      return this.currentFile && !this.uploadError && !this.uploading;
    },
    preloaded() {
      return this.preloadedFile && !this.empty && !this.uploading && !this.uploadError && !this.done;
    },
    currentFile() {
      return this.files.length > 0 && this.files[0];
    },
    uploadProgress() {
      return this.currentFile && Number(this.currentFile.progress);
    },
    error() {
      return this.validationErrorMessage && !this.uploading;
    },
    /* Upload states END */

    dropZoneTakePhotoColor() {
      return this.error ? 'riseup-danger-red' : 'riseup-blue';
    },
    dropZoneTextClass() {
      return this.error ? 'text-riseup-danger-red' : 'text-riseup-blue';
    },
  },
  methods: {
    acceptableMimeType(uploadedFileMimeType) {
      const acceptableMimeTypes = this.accept.mimeType.split(',');
      return _.some(acceptableMimeTypes, acceptableMimeType => {
        const [type, extension] = acceptableMimeType.split('/');
        if (extension === '*') {
          return uploadedFileMimeType.split('/')[0] === type;
        }
        return uploadedFileMimeType === acceptableMimeType;
      });
    },
    createPreviewUrl(newFile) {
      const URL = window.URL || window.webkitURL;
      if (URL && URL.createObjectURL) {
        // eslint-disable-next-line no-param-reassign
        newFile.url = URL.createObjectURL(newFile.file);
      }
    },
    inputFilter(newFile, oldFile, prevent) {
      if (!oldFile && newFile) {
        if (newFile.file && !this.acceptableMimeType(newFile.file.type)) {
          prevent();
          this.$q.dialog({
            component: FileTypeUnsupported,
            parent: this,
          });
          return;
        }

        this.createPreviewUrl(newFile);

        // eslint-disable-next-line no-param-reassign
        newFile.error = this.COMPRESSING_MARKER;
        // eslint-disable-next-line no-new
        new Compressor(newFile.file, {
          maxWidth: 2400,
          maxHeight: 2400,

          success: result => {
            this.$refs.upload.update(newFile, { file: result, error: '', size: result.size, type: result.type });
          },
          error: error => {
            this.$refs.upload.update(newFile, { error: error.message });
          },
        });
      }
    },
    inputFile(newFile, oldFile) {
      if (newFile && oldFile) {
        // Reset validation error every time a file is selected
        this.reset();

        // Overwrite existing file
        if (newFile.active !== oldFile.active && newFile.active) {
          // Remove the old file
          this.$emit('removed');
        }

        // Uploaded successfully
        if (newFile.success !== oldFile.success) {
          this.uploaded(newFile);
        }
      }

      // Automatically start uploading the selected file
      if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
        if (!this.$refs.upload.active) {
          this.$refs.upload.active = true;
        }
      }
    },

    uploaded(file) {
      const { versionId } = file.response;
      this.$emit('uploaded', { fileVersion: versionId });
    },
    removeFile() {
      if (this.currentFile) {
        this.$refs.upload.remove(this.currentFile);
      }
      this.$emit('removed');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/styles/riseup.brand";

$dropZoneHeight: 296px;
$dropZoneBorderWidth: 1px;

@mixin dropZoneBorder($color, $dashed) {
  border-color: $color;
  @if($dashed) {
    border-style: dashed;
  } @else {
    border-style: solid;
  }
  border-width: $dropZoneBorderWidth;
}

.uploader {
  margin-bottom: 16px;

  .drop-zone {
    height: $dropZoneHeight;
    border-radius: 2px;

    &.empty {
      @include dropZoneBorder($riseup-blue, true);
    }

    &.preloaded {
      height: 100%;
      padding: 16px;
      @include dropZoneBorder($riseup-blue, false);

      .preloaded-image {
        margin-right: 25px;
      }
    }

    &.uploading {
      @include dropZoneBorder($riseup-blue, true);
      background: linear-gradient(180deg, $riseup-light_blue 0%, rgba($riseup-light_blue, 0) 100%);
    }

    &.uploadError {
      @include dropZoneBorder($riseup-danger-red, true);
      background: linear-gradient(180deg, $riseup_danger_light_red 0%, rgba($riseup_danger_light_red, 0) 100%);
    }

    &.done {
      @include dropZoneBorder($riseup-blue, false);
      position: relative;

      .success-badge {
        position: absolute;
        top: 5px;
        left: 5px;
      }

      .image-preview {
        width: 100%;
        height: calc(#{$dropZoneHeight} - #{$dropZoneBorderWidth} * 2);

        img {
          object-fit: contain;
          width: 100%;
          height: 100%;
        }
      }
    }

    &.validationError {
      @include dropZoneBorder($riseup-danger-red, true);
    }

    .camera-icon {
      margin-bottom: 16px;
    }
  }

  .status-bar {
    margin-top: 10px;

    .progress-text {
      margin-top: 10px;
    }
  }
}
</style>
